import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Boxes,
  Gauge,
  Share2,
  ShieldCheck,
  Store,
  Target,
  Zap,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const FeatureTile = ({ icon: Icon, title, desc, accent = 'orange' }) => (
  <div className="neon-card rounded-md p-5 bracket-corners">
    <div
      className={`w-10 h-10 rounded grid place-items-center mb-3 ${
        accent === 'cyan' ? 'bg-cyan-400/10 border border-cyan-400/40' : 'bg-orange-500/10 border border-orange-500/40'
      }`}
    >
      <Icon className={`w-5 h-5 ${accent === 'cyan' ? 'text-cyan-300' : 'text-orange-300'}`} />
    </div>
    <h3 className="font-mono-display text-lg text-white mb-1">{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
  </div>
);

const Step = ({ num, title, desc, accent = 'orange' }) => (
  <div className="flex gap-4">
    <div
      className={`w-10 h-10 shrink-0 grid place-items-center rounded border font-mono-display ${
        accent === 'red'
          ? 'border-red-500/50 bg-red-500/10 text-red-300'
          : 'border-orange-500/50 bg-orange-500/10 text-orange-300'
      }`}
    >
      {num}
    </div>
    <div>
      <h3 className="font-mono-display text-white mb-1">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
    </div>
  </div>
);

export default function About() {
  const { isAuthed } = useAuth();
  const primaryPath = isAuthed ? '/dashboard' : '/auth/register';

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 py-10 sm:py-12">
        <Link to="/" className="btn-ghost inline-flex mb-6">
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to home
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-200 text-xs uppercase tracking-widest mb-4">
              <Zap className="w-3.5 h-3.5" />
              ValoRex // about
            </div>
            <h1 className="font-mono-display text-3xl sm:text-4xl lg:text-5xl text-white mb-4 leading-tight">
              Built for <span className="neon-orange">operators</span>
              <br />
              who <span className="neon-cyan">flex</span> their vault
            </h1>
            <p className="text-zinc-300/90 text-base sm:text-lg leading-relaxed max-w-xl mb-6">
              ValoRex is a Valorant account vault and showcase platform. Link your accounts, track inventory and
              stats, share public profiles, and manage everything from one cyberpunk-grade command center.
            </p>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-xl mb-8">
              Inspired by the need for a cleaner, faster way to preview loadouts and account value—without juggling
              screenshots or logging in every time—we built ValoRex for collectors, sellers, and serious players in
              the community.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={primaryPath} className="btn-neon">
                {isAuthed ? 'Dashboard' : 'Get Started'} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" className="btn-ghost">
                View pricing
              </Link>
            </div>
          </div>

          <div className="neon-card rounded-lg p-6 sm:p-8 bracket-corners">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 grid place-items-center rounded border border-orange-500/60 bg-orange-500/10">
                <Target className="w-6 h-6 text-orange-300" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-cyan-300">Our mission</div>
                <h2 className="font-mono-display text-xl text-white">One vault. Every account.</h2>
              </div>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed mb-4">
              Give Valorant players a secure, beautiful hub to sync account data, understand what they own, and share
              proof of their collection with a single link.
            </p>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 shrink-0" />
                Real inventory, store, and rank data via Riot&apos;s Access URL flow
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 mt-2 shrink-0" />
                Shareable public pages for trading, flexing, or portfolio review
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 shrink-0" />
                Manual PKR billing built for the local community—no hidden gateways
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs uppercase tracking-widest text-cyan-300 mb-2">// what we offer</div>
            <h2 className="font-mono-display text-3xl sm:text-4xl text-white">
              Why you should choose <span className="neon-orange">ValoRex</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureTile
              icon={Boxes}
              title="Account vault"
              desc="Link multiple Valorant accounts, browse skins, agents, buddies, and cards from one dashboard."
            />
            <FeatureTile
              icon={Share2}
              title="Public showcase"
              desc="Generate share links so buyers, friends, or teammates can view your profile without logging in."
              accent="cyan"
            />
            <FeatureTile
              icon={Store}
              title="Store intel"
              desc="Check daily shop, Night Market, and featured bundles with timers—no client required."
            />
            <FeatureTile
              icon={Gauge}
              title="Deep stats"
              desc="Ranks, match history, VP totals, and account metadata kept in sync when you refresh."
              accent="cyan"
            />
            <FeatureTile
              icon={ShieldCheck}
              title="Security first"
              desc="Credentials stay encrypted. We never publish tokens or sensitive auth data on share pages."
            />
            <FeatureTile
              icon={Zap}
              title="Built for speed"
              desc="A focused UI with slot limits and plan tiers so power users can scale their vault cleanly."
              accent="cyan"
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <div className="neon-card-red-hover rounded-lg p-6 sm:p-8 bracket-corners">
            <div className="text-xs uppercase tracking-widest text-cyan-300 mb-2">// how it works</div>
            <h2 className="font-mono-display text-2xl text-white mb-6">Three steps to your vault</h2>
            <div className="space-y-6">
              <Step
                num="01"
                title="Create your operator account"
                desc="Sign up free, pick a plan when you're ready, and open your dashboard."
              />
              <Step
                num="02"
                title="Link via Access URL"
                desc="Paste Riot's secure Access URL—we parse entitlements and sync your account data."
              />
              <Step
                num="03"
                title="Manage and share"
                desc="Browse inventory, track cooldowns on slot limits, and copy a public share link anytime."
              />
            </div>
          </div>

          <div className="neon-card-red-hover rounded-lg p-6 sm:p-8 bracket-corners border-cyan-400/20">
            <div className="text-xs uppercase tracking-widest text-orange-300 mb-2">// independence</div>
            <h2 className="font-mono-display text-2xl text-white mb-4">Not affiliated with Riot</h2>
            <p className="text-sm text-zinc-300 leading-relaxed mb-4">
              ValoRex is an independent project. We are not endorsed by, sponsored by, or affiliated with Riot Games,
              Inc. Valorant and all related names and marks are trademarks of Riot Games, Inc.
            </p>
            <p className="text-sm text-zinc-400 leading-relaxed mb-6">
              ValoRex uses publicly available game data sources and is subject to their respective terms and policies. We comply with all applicable usage rules. All game data is provided by third-party services and may change or become unavailable at any time.
            </p>
            <Link to="/terms-of-service" className="btn-ghost inline-flex">
              Read terms of service
            </Link>
          </div>
        </div>

        <div className="neon-card rounded-lg p-8 sm:p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-cyan-400/5 pointer-events-none" />
          <Zap className="w-8 h-8 mx-auto mb-3 text-orange-300 relative" />
          <h3 className="font-mono-display text-2xl sm:text-3xl text-white mb-2 relative">Ready to open your vault?</h3>
          <p className="text-zinc-400 max-w-xl mx-auto mb-6 relative text-sm sm:text-base">
            Join operators who use ValoRex to track, share, and flex their Valorant accounts.
          </p>
          <Link to={primaryPath} className="btn-neon relative inline-flex">
            {isAuthed ? 'Go to Dashboard' : 'Create free account'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
